import React, { createContext, useReducer, useContext, useCallback, useEffect } from 'react';
import { loginUser, registerUser, logoutUser, getAccessToken, clearAuthTokens } from '../services/userService';

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false
      };
    default:
      return state;
  }
};

export const AuthContext = createContext(initialState);

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        await getAccessToken();
        if (isMounted) {
          dispatch({ type: 'AUTH_SUCCESS', payload: {} });
        }
      } catch (error) {
        clearAuthTokens();
        if (isMounted) {
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (credentials) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const data = await loginUser(credentials.email, credentials.password);
      dispatch({ type: 'AUTH_SUCCESS', payload: data });
      return data;
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const register = useCallback(async (userData) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const data = await registerUser(userData);
      dispatch({ type: 'LOGOUT' });
      return data;
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  const value = {
    ...state,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
